import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import InputWrapper from 'shared/components/InputWrapper'
import resUserAccount from './resolvers/resUserAccount'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from './utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import { roles } from 'consts'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import { togglePasswordInput, generateRandomPassword } from 'utils/passwordUtils'
import * as Icon from 'react-bootstrap-icons'
import hasAnyRole from 'shared/utils/hasAnyRole'
import LayoutSwitcher from 'components/LayoutSwitcher'
import { ReactReduxContext } from 'react-redux'
import HeaderFilterRoleSelect from 'containers/HeaderFilterRoleSelect'

const submitRef = React.createRef<HTMLButtonElement>()

const UserAccount: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)

  const [passwordInputType, setPasswordInputType] = useState<string>('password')

  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

  const [filterRole, setCurrentFilterRole] = useState<{} | null>({})

  const { store } = useContext(ReactReduxContext)

  const navigate = useNavigate()

  const {
    control,
    register,
    getValues,
    setValue,
    handleSubmit,
    reset,
    setFocus,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: resUserAccount,
  })

  useInit({
    getValues,
    reset,
    setDataLoaded,
    setShowOverlay,
    currentCustomer,
  })

  useFocusError({
    errors,
    setFocus,
  })

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      showCardHeader={true}
      headerInsert={
        <>
          <HeaderFilterRoleSelect
            setShowOverlay={setShowOverlay}
            handleFilterRoleChanged={(filterRole) => setCurrentFilterRole(filterRole)}
          />
        </>
      }
      actionInsert={
        <Button
          disabled={!dataLoaded}
          type='submit'
          name='save'
          className='btn-round btn-primary'
          onClick={() => {
            if (submitRef.current != null) {
              submitRef.current.click()
            }
          }}
        >
          Save
        </Button>
      }
      menuBottomInsert={hasAnyRole([roles.superAdmin]) ? <LayoutSwitcher store={store} /> : false}
    >
      <>
        <Form
          onSubmit={handleSubmit((data) =>
            prepareSubmitForm({
              submitForm,
              navigate,
              data,
              setShowOverlay,
              setError,
              id: undefined,
              name: 'User',
              successNav: '',
            }),
          )}
          id='main-form'
        >
          <InputWrapper label='Firstname' name='firstname' errors={errors}>
            <Form.Control type='text' {...register('firstname')} />
          </InputWrapper>

          <InputWrapper label='Lastname' name='lastname' errors={errors}>
            <Form.Control type='text' {...register('lastname')} />
          </InputWrapper>

          <InputWrapper label='Email' name='email' errors={errors}>
            <Form.Control type='email' {...register('email')} />
          </InputWrapper>

          <InputWrapper label='New Password' name='password_api' errors={errors}>
            <InputGroup className='mb-3'>
              <Form.Control type={passwordInputType} {...register('password_api')} />
              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Generate Password</Tooltip>}
              >
                <Button
                  variant='outline-primary'
                  onClick={() => setValue('password_api', generateRandomPassword())}
                >
                  <Icon.Key title='Generate random password' />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                overlay={
                  <Tooltip>
                    {passwordInputType === 'password' ? 'Show password' : 'Hide password'}
                  </Tooltip>
                }
              >
                <Button
                  variant='outline-warning'
                  onClick={() => setPasswordInputType(togglePasswordInput(passwordInputType))}
                >
                  {passwordInputType === 'password' ? <Icon.Unlock /> : <Icon.Lock />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
            <Form.Text id='passwordHelpBlock' muted>
              Leave New Password field blank, if you don't want to change your current password.
            </Form.Text>
          </InputWrapper>

          <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>
        </Form>
      </>
    </Layout>
  )
}

export default UserAccount
